import React from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Users, TrendingUp, CalendarCheck, ArrowRight, Quote, MapPin, Phone, Mail } from 'lucide-react';

const AboutUsPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-gray-950 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-logo-blue/20 via-transparent to-logo-cyan/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight" data-testid="about-hero-title">
            {t('about_heroTitle')}
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-testid="about-hero-subtitle">
            {t('about_heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Founder Introduction */}
      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            {/* Portrait */}
            <div className="lg:col-span-2 flex justify-center">
              <div className="relative">
                <div className="w-64 h-80 sm:w-72 sm:h-96 rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="/ceo-photo.jpg"
                    alt={t('about_founderName')}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Intro Text */}
            <div className="lg:col-span-3">
              <p className="text-sm font-semibold text-logo-blue uppercase tracking-widest mb-3">
                {t('about_founderTitle')}
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-1" data-testid="about-founder-name">
                {t('about_founderName')}
              </h2>
              <p className="text-gray-500 mb-8 text-base">{t('about_founderRole')}</p>

              {/* Our Story */}
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-0.5 bg-logo-blue inline-block"></span>
                {t('about_storyTitle')}
              </h3>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>{t('about_storyP1')}</p>
                <p>{t('about_storyP2')}</p>
                <p className="font-semibold text-gray-800 italic">{t('about_storyP3')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 text-center" data-testid="about-vision-title">
            {t('about_visionTitle')}
          </h3>
          <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
            <p className="text-xl font-medium text-gray-800">{t('about_visionP1')}</p>
            <p>{t('about_visionP2')}</p>
            <p>{t('about_visionP3')}</p>
          </div>
        </div>
      </section>

      {/* Promise */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 text-center" data-testid="about-promise-title">
            {t('about_promiseTitle')}
          </h3>
          <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
            <p>{t('about_promiseP1')}</p>
            <p>{t('about_promiseP2')}</p>
            <p className="bg-blue-50 border-l-4 border-logo-blue p-5 rounded-r-lg text-gray-800 font-medium">
              {t('about_promiseP3')}
            </p>
            <p className="italic text-gray-700">{t('about_promiseP4')}</p>
          </div>
        </div>
      </section>

      {/* Core Team */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3" data-testid="about-core-team-title">
              {t('about_coreTeamTitle')}
            </h3>
            <p className="text-gray-500 text-lg">{t('about_coreTeamSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { img: '/team-murshid.jpg', name: t('about_team1Name'), role: t('about_team1Role'), desc: t('about_team1Desc') },
              { img: '/team-ruhina.jpg', name: t('about_team2Name'), role: t('about_team2Role'), desc: t('about_team2Desc') },
              { img: '/team-mubbasira.jpg', name: t('about_team3Name'), role: t('about_team3Role'), desc: t('about_team3Desc') },
            ].map((member) => (
              <div key={member.name} className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
                <div className="h-72 overflow-hidden">
                  <img src={member.img} alt={member.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-6">
                  <h4 className="text-xl font-bold text-gray-900">{member.name}</h4>
                  <p className="text-sm font-semibold text-logo-blue mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{member.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Highlight Quote */}
      <section className="py-20 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-logo-blue/10 via-transparent to-logo-cyan/10"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Quote className="w-12 h-12 text-logo-cyan/40 mx-auto mb-6" />
          <blockquote className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-testid="about-quote">
            {t('about_quote')}
          </blockquote>
          <div className="mt-8 flex justify-center">
            <div className="w-16 h-1 bg-gradient-to-r from-logo-blue to-logo-cyan rounded-full"></div>
          </div>
          <p className="mt-4 text-gray-400">— {t('about_founderName')}</p>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-shadow duration-300" data-testid="about-metric-clients">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-5">
                <Users className="w-8 h-8 text-logo-blue" />
              </div>
              <p className="text-4xl sm:text-5xl font-bold text-gray-900 mb-2">15,000+</p>
              <p className="text-gray-500 font-medium">{t('about_metricClients')}</p>
            </div>
            <div className="text-center p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-shadow duration-300" data-testid="about-metric-success">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-5">
                <TrendingUp className="w-8 h-8 text-logo-blue" />
              </div>
              <p className="text-4xl sm:text-5xl font-bold text-gray-900 mb-2">98%+</p>
              <p className="text-gray-500 font-medium">{t('about_metricSuccess')}</p>
            </div>
            <div className="text-center p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-shadow duration-300" data-testid="about-metric-appointments">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-5">
                <CalendarCheck className="w-8 h-8 text-logo-blue" />
              </div>
              <p className="text-4xl sm:text-5xl font-bold text-gray-900 mb-2">7</p>
              <p className="text-gray-500 font-medium">{t('about_metricAppointments')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-gradient-to-r from-logo-blue to-logo-cyan">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl sm:text-3xl font-bold text-white mb-8" data-testid="about-closing-text">
            {t('about_closingText')}
          </p>
          <a
            href="https://wa.me/971585862103?text=Hello%2C%20I%20would%20like%20to%20start%20my%20visa%20journey%20with%20The%20Visagram"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-logo-blue px-10 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all hover:scale-105"
            data-testid="about-cta-button"
          >
            {t('about_closingCta')}
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Office Location */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('about_officeTitle')}</h3>
          <div className="bg-white rounded-2xl shadow-md p-8 inline-block">
            <div className="flex items-center justify-center gap-2 mb-3">
              <MapPin className="w-5 h-5 text-logo-blue" />
              <p className="font-bold text-logo-blue text-lg">The Visagram - B Hive</p>
            </div>
            <p className="text-gray-600">Level 3 Burjuman Mall</p>
            <p className="text-gray-600 mb-4">Khalid Bin Al Waleed Rd - Dubai UAE</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-500">
              <a href="tel:+971585862103" className="flex items-center gap-1.5 hover:text-logo-blue transition-colors">
                <Phone className="w-4 h-4" /> +971 58 586 2103
              </a>
              <span className="hidden sm:inline text-gray-300">|</span>
              <a href="mailto:info@thevisagram.ae" className="flex items-center gap-1.5 hover:text-logo-blue transition-colors">
                <Mail className="w-4 h-4" /> info@thevisagram.ae
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUsPage;
